import React from 'react';
import { StepperProps } from '../../../types/DesignSystem/Stepper';
import { Check } from '../Iconography/Icons';
import { FOCUS_RING } from '../focusRing';
import { withClassName } from '../cn';

type StepStatus = 'complete' | 'current' | 'upcoming';

/* The state in words, after the label: the indicator's glyph and colour say it only to the eye.
   The current step says it through `aria-current="step"`, which screen readers announce. */
const STATUS_TEXT: Record<StepStatus, string | null> = {
  complete: 'completed',
  current: null,
  upcoming: 'not started',
};

/* Every indicator keeps a 2px border, transparent on the filled one, so under forced colors
   (which drop the fill) all three states still draw their ring. There the current ring takes
   Highlight, or it would be the same black ring as an upcoming one (measured). */
const INDICATOR: Record<StepStatus, string> = {
  complete: 'tw-bg-accent-bold tw-border-transparent tw-text-ink-inverse',
  current: 'tw-bg-transparent tw-border-accent tw-text-accent forced-colors:tw-border-[color:Highlight]',
  upcoming: 'tw-bg-transparent tw-border-edge-strong tw-text-ink-tertiary',
};

/**
 * Stepper (§ 372) — see the type file for why it exists.
 *
 * An ordered list in a `nav` named "Progress": the list gives position ("2 of 3") for
 * free, `aria-current="step"` marks where the user is, and the other steps carry their
 * state as words. The numbers and the tick are `aria-hidden`: the list already counts.
 *
 * Only COMPLETED steps navigate, and only when `onStepChange` is passed. Going forward is
 * the flow's decision (its Next button validates); a stepper that let you jump ahead would
 * skip that validation. An upcoming step is plain text, never a disabled button: a disabled
 * button is unfocusable, which is how the Recategorize stepper hid all of its steps.
 */
const Stepper = React.forwardRef<HTMLElement, StepperProps>(function Stepper({
  steps,
  current,
  onStepChange,
  orientation = 'horizontal',
  className = '',
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...rest
}, ref) {
  const currentIndex = steps.findIndex((step) => step.id === current);
  const vertical = orientation === 'vertical';

  return (
    <nav
      {...rest}
      ref={ref}
      aria-label={ariaLabelledBy ? ariaLabel : (ariaLabel ?? 'Progress')}
      aria-labelledby={ariaLabelledBy}
      data-orientation={orientation}
      data-testid={dataTestId}
      className={withClassName('tw-w-full', className)}
    >
      <ol className={`tw-m-0 tw-p-0 tw-list-none tw-flex ${vertical ? 'tw-flex-col' : 'tw-items-start'}`}>
        {steps.map((step, index) => {
          const status: StepStatus = index < currentIndex ? 'complete' : index === currentIndex ? 'current' : 'upcoming';
          const last = index === steps.length - 1;
          const navigable = status === 'complete' && onStepChange !== undefined;
          const stepId = dataTestId ? `${dataTestId}-step-${step.id}` : undefined;
          /* A line is a fill, and forced colors drop author fills: the connectors vanished there
             (measured). System colours survive — Highlight for the distance covered, GrayText ahead. */
          const connector = `${status === 'complete'
            ? 'tw-bg-accent forced-colors:tw-bg-[color:Highlight]'
            : 'tw-bg-edge forced-colors:tw-bg-[color:GrayText]'} tw-shrink-0`;

          const indicator = (
            <span
              aria-hidden="true"
              className={`tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full tw-border-2 tw-text-sub-title-label ${INDICATOR[status]}`}
            >
              {status === 'complete' ? <Check size={14} /> : index + 1}
            </span>
          );
          const text = (
            <span className="tw-flex tw-min-w-0 tw-flex-col tw-pt-0.5 tw-text-left">
              <span
                className={`tw-text-title ${
                  navigable ? 'tw-text-accent group-hover:tw-underline' : status === 'upcoming' ? 'tw-text-ink-secondary' : 'tw-text-ink'
                }`}
              >
                {step.label}
                {STATUS_TEXT[status] && <span className="tw-sr-only">, {STATUS_TEXT[status]}</span>}
              </span>
              {step.description !== undefined && (
                <span className="tw-mt-0.5 tw-text-caption-metadata tw-text-ink-tertiary">{step.description}</span>
              )}
            </span>
          );
          const body = navigable ? (
            <button
              type="button"
              onClick={() => onStepChange?.(step.id)}
              data-testid={stepId ? `${stepId}-button` : undefined}
              className={`tw-group tw-flex tw-items-start tw-gap-2 tw-min-w-0 -tw-m-1 tw-p-1 tw-rounded-ctl tw-bg-transparent tw-border-0 tw-cursor-pointer ${FOCUS_RING}`}
            >
              {indicator}
              {text}
            </button>
          ) : (
            <span className="tw-flex tw-items-start tw-gap-2 tw-min-w-0">
              {indicator}
              {text}
            </span>
          );

          return (
            <li
              key={step.id}
              aria-current={status === 'current' ? 'step' : undefined}
              data-status={status}
              data-testid={stepId}
              className={vertical ? 'tw-flex tw-gap-2' : `tw-flex tw-items-start tw-min-w-0 ${last ? '' : 'tw-flex-1'}`}
            >
              {vertical ? (
                <>
                  {/* The rail: the indicator, then (between steps) the line down to the next one. */}
                  <span className="tw-flex tw-flex-col tw-items-center" aria-hidden="true">
                    {indicator}
                    {!last && <span className={`${connector} tw-w-px tw-flex-1 tw-min-h-4 tw-my-1`} />}
                  </span>
                  <span className={`tw-flex tw-min-w-0 ${last ? '' : 'tw-pb-6'}`}>
                    {navigable ? (
                      <button
                        type="button"
                        onClick={() => onStepChange?.(step.id)}
                        data-testid={stepId ? `${stepId}-button` : undefined}
                        className={`tw-group tw-flex tw-min-w-0 -tw-m-1 tw-p-1 tw-rounded-ctl tw-bg-transparent tw-border-0 tw-cursor-pointer ${FOCUS_RING}`}
                      >
                        {text}
                      </button>
                    ) : text}
                  </span>
                </>
              ) : (
                <>
                  {body}
                  {!last && <span aria-hidden="true" className={`${connector} tw-h-px tw-flex-1 tw-min-w-6 tw-mx-3 tw-mt-3`} />}
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
});

export default Stepper;
